import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  AnalyticsGranularity,
  AnalyticsKpi,
  AnalyticsResponse,
  AnalyticsResponseValue
} from "src/app/backend/models/analytics";
import { environment } from "src/environments/environment";

@Injectable()
export class KpiBackend {
  private readonly http = inject(HttpClient);

  satisfaction({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/satisfaction/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  satisfactionValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/satisfaction`
    );
  }

  customerGrowthRate({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/customer-growth-rate/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  customerGrowthRateValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/customer-growth-rate`
    );
  }

  checkedIn({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/checked-in/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  checkedInValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/checked-in`
    );
  }

  waitingTime({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/waiting-time/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  waitingTimeValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/waiting-time`
    );
  }

  duration({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/duration/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  durationValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/duration`
    );
  }

  averageRevenuePerInvoice({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/average-revenue-per-invoice/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  averageRevenuePerInvoiceValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/average-revenue-per-invoice`
    );
  }

  averageRevenuePerCustomer({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/average-revenue-per-customer/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  averageRevenuePerCustomerValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/average-revenue-per-customer`
    );
  }

  averageRevenuePerPatient({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/average-revenue-per-patient/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  averageRevenuePerPatientValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/average-revenue-per-patient`
    );
  }

  averageRevenuePerMember({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/average-revenue-per-member/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  averageRevenuePerMemberValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/average-revenue-per-member`
    );
  }

  medicalServicePerCustomer({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/medical-service-per-customer/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  medicalServicePerCustomerValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/medical-service-per-customer`
    );
  }

  medicalServicePerPatient({
    from,
    to,
    granularity
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity;
  }): Observable<AnalyticsResponse<AnalyticsKpi>> {
    return this.http
      .get<AnalyticsResponse<AnalyticsKpi>>(
        `${environment.snoots.api.analytics}/kpis/medical-service-per-patient/histories`,
        {
          params: {
            from,
            to,
            granularity
          }
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        }))
      );
  }

  medicalServicePerPatientValue(): Observable<AnalyticsResponseValue> {
    return this.http.get<AnalyticsResponseValue>(
      `${environment.snoots.api.analytics}/kpis/medical-service-per-patient`
    );
  }
}
